import type { StaticDatabaseHandle } from "@-/dev.improve.haniverse.database.static";
import {
  getHskCharacterCountsByLevel,
  getHskWordCountsByLevel,
  type HskVersionKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import arrayLength from "@-/com.npmjs.extra-array__length";

export type HskLevelStats = {
  characters: number;
  level: string;
  words: number;
};

export type HskVersionStats = {
  description: string;
  label: string;
  levelStats: readonly HskLevelStats[];
  totalCharacters: number;
  totalLevels: number;
  totalWords: number;
  version: string;
  yearLabel: string;
};

/**
 * Static, non-bundle-bloating metadata per HSK version, plus the stored level values to read out
 * of the static DB. The actual char/word counts come from the DB ({@link getHskVersionStats}) so
 * the raw HSK JSON lists no longer need to be bundled.
 */
const HSK_VERSION_META: Record<
  HskVersionKey,
  {
    description: string;
    label: string;
    /** [display label, stored DB level] for each level row, in display order. */
    levels: readonly (readonly [string, number])[];
    yearLabel: string;
  }
> = {
  hsk2: {
    description:
      "The original six-level standard introduced in 2010. Still widely used by textbooks and existing test prep materials.",
    label: "HSK 2.0",
    levels: [
      ["Level 1", 1],
      ["Level 2", 2],
      ["Level 3", 3],
      ["Level 4", 4],
      ["Level 5", 5],
      ["Level 6", 6],
    ],
    yearLabel: "2010",
  },
  hsk2026: {
    description:
      "The 2026 revision of HSK 3.0 (HSK 3.1). Refines the character and word lists; matches what new exams will assess.",
    label: "New HSK",
    levels: [
      ["Level 1", 1],
      ["Level 2", 2],
      ["Level 3", 3],
      ["Level 4", 4],
      ["Level 5", 5],
      ["Level 6", 6],
      ["Level 7-9", 7],
    ],
    yearLabel: "2026",
  },
  hsk3: {
    description:
      "Released in 2021 with nine levels (six classroom levels plus an advanced 7-9 tier). Larger vocabulary and broader scope than HSK 2.0.",
    label: "HSK 3.0",
    levels: [
      ["Level 1", 1],
      ["Level 2", 2],
      ["Level 3", 3],
      ["Level 4", 4],
      ["Level 5", 5],
      ["Level 6", 6],
      ["Level 7-9", 7],
    ],
    yearLabel: "2021",
  },
};

export const HSK_VERSION_ORDER = [
  "hsk2",
  "hsk3",
  "hsk2026",
] as const satisfies readonly HskVersionKey[];

/** Narrows an untyped value (e.g. a tab's `onValueChange` string) to an HSK version key. */
export function isHskVersionKey(value: string): value is HskVersionKey {
  return value === "hsk2" || value === "hsk3" || value === "hsk2026";
}

/** Display label for an HSK version (e.g. "HSK 2.0", "HSK 3.0", "New HSK"). Pure metadata. */
export function getHskVersionLabel(version: HskVersionKey): string {
  return HSK_VERSION_META[version].label;
}

function buildVersionStats(
  version: HskVersionKey,
  charCounts: Map<number, number>,
  wordCounts: Map<number, number>,
): HskVersionStats {
  const meta = HSK_VERSION_META[version];
  const levelStats: HskLevelStats[] = meta.levels.map(([label, storedLevel]) => ({
    characters: charCounts.get(storedLevel) ?? 0,
    level: label,
    words: wordCounts.get(storedLevel) ?? 0,
  }));
  let totalCharacters = 0;
  let totalWords = 0;
  for (const row of levelStats) {
    totalCharacters += row.characters;
    totalWords += row.words;
  }
  return {
    description: meta.description,
    label: meta.label,
    levelStats,
    totalCharacters,
    totalLevels: arrayLength(levelStats),
    totalWords,
    version,
    yearLabel: meta.yearLabel,
  };
}

async function getVersionStats(
  db: StaticDatabaseHandle,
  version: HskVersionKey,
): Promise<HskVersionStats> {
  const [charCounts, wordCounts] = await Promise.all([
    getHskCharacterCountsByLevel(db, version),
    getHskWordCountsByLevel(db, version),
  ]);
  return buildVersionStats(version, charCounts, wordCounts);
}

/**
 * Per-version HSK character/word counts, read from the shipped static DB rather than the bundled
 * HSK JSON lists. Every version is present, so lookups by `HskVersionKey` need no fallback.
 */
export async function getHskVersionStats(
  db: StaticDatabaseHandle,
): Promise<Record<HskVersionKey, HskVersionStats>> {
  const hsk2 = await getVersionStats(db, "hsk2");
  const hsk3 = await getVersionStats(db, "hsk3");
  const hsk2026 = await getVersionStats(db, "hsk2026");
  return { hsk2, hsk2026, hsk3 };
}
